import {
  Box,
  LinearProgress,
  Typography,
  Fade
} from '@mui/material';
import { DollarSpinner } from './DollarSpinner';

/**
 * Centered loading spinner with optional message
 */
export const CenteredLoader = ({ message = "Loading...", size = 40 }) => {
  // Map numeric size to DollarSpinner size
  const spinnerSize = size >= 50 ? 'large' : size >= 30 ? 'medium' : 'small';
  
  return (
    <Box 
      display="flex" 
      flexDirection="column"
      justifyContent="center" 
      alignItems="center" 
      minHeight="200px"
      gap={2}
    >
      <DollarSpinner size={spinnerSize} />
      {message && (
        <Typography variant="body2" color="textSecondary">
          {message}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Inline loading indicator for buttons and small spaces
 */
export const InlineLoader = ({ message }) => {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <DollarSpinner size="small" />
      {message && (
        <Typography variant="body2" color="textSecondary">
          {message}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Progress bar for operations with known progress
 */
export const ProgressLoader = ({ progress, message, showPercentage = true }) => {
  return (
    <Box sx={{ width: '100%' }}>
      {message && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="textSecondary">
            {message}
          </Typography>
          {showPercentage && (
            <Typography variant="body2" color="textSecondary">
              {Math.round(progress)}%
            </Typography>
          )}
        </Box>
      )}
      <LinearProgress variant="determinate" value={progress} />
    </Box>
  );
};

/**
 * Overlay loader for full-screen loading states
 */
export const OverlayLoader = ({ message = "Loading...", backdrop = true }) => {
  return (
    <Fade in={true}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: backdrop ? 'rgba(10, 11, 13, 0.5)' : 'transparent', // §2.8: scrim tracks near-black
          zIndex: 1300,
          gap: 2
        }}
      >
        <DollarSpinner size="large" />
        <Typography variant="h6" color={backdrop ? '#fbfbfc' : 'textPrimary'}>
          {message}
        </Typography>
      </Box>
    </Fade>
  );
};

/**
 * Button loading state
 */
export const ButtonLoader = ({ loading, children }) => {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      {loading && <DollarSpinner size="small" />}
      {children}
    </Box>
  );
};

/**
 * Card loading overlay
 */
export const CardLoader = ({ loading, children, message = "Loading..." }) => {
  return (
    <Box position="relative">
      {children}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(251, 251, 252, 0.8)', // §2.8: wash tracks off-white
            borderRadius: 1,
            gap: 1
          }}
        >
          <DollarSpinner size="medium" />
          <Typography variant="body2" color="textSecondary">
            {message}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default {
  CenteredLoader,
  InlineLoader,
  ProgressLoader,
  OverlayLoader,
  ButtonLoader,
  CardLoader
};
